import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";
import { PerPhotoType } from "../utils/enums";
import { formatToBase64ImageUrl } from "../utils/functions";

const initialState = {
	inspections: [],
	loading: false,
	error: null,
	total: 0,
};

const FETCH_INSPECTIONS_REQUEST = "FETCH_INSPECTIONS_REQUEST";
const FETCH_INSPECTIONS_SUCCESS = "FETCH_INSPECTIONS_SUCCESS";
const FETCH_INSPECTIONS_FAILURE = "FETCH_INSPECTIONS_FAILURE";
const CREATE_INSPECTION_REQUEST = "CREATE_INSPECTION_REQUEST";
const CREATE_INSPECTION_SUCCESS = "CREATE_INSPECTION_SUCCESS";
const CREATE_INSPECTION_FAILURE = "CREATE_INSPECTION_FAILURE";
const UPDATE_INSPECTION_REQUEST = "UPDATE_INSPECTION_REQUEST";
const UPDATE_INSPECTION_SUCCESS = "UPDATE_INSPECTION_SUCCESS";
const UPDATE_INSPECTION_FAILURE = "UPDATE_INSPECTION_FAILURE";
const DELETE_INSPECTION_REQUEST = "DELETE_INSPECTION_REQUEST";
const DELETE_INSPECTION_SUCCESS = "DELETE_INSPECTION_SUCCESS";
const DELETE_INSPECTION_FAILURE = "DELETE_INSPECTION_FAILURE";

// Helper to save inspection photos using personnel-photos service
const saveInspectionPhotos = async (
	inspectionId,
	photos,
	userAgency,
	userId,
) => {
	try {
		const service = getService(serviceNames.personnelPhotos);

		// Get existing photos for this inspection
		const existingPhotos = await service.find({
			query: {
				entityId: inspectionId,
				photoFor: PerPhotoType.Inspection,
				isDeleted: false,
			},
		});

		const existingData = existingPhotos || [];
		const existingImageIds = existingData.map((photo) => photo.imageID);
		const newPhotos = photos || [];

		// Photos with numeric imageID that exists in DB are existing photos
		const newPhotoImageIds = newPhotos
			.filter(
				(p) =>
					typeof p.id === "number" &&
					existingImageIds.includes(p.id),
			)
			.map((p) => p.id);

		// Find photos to delete (exist in DB but not in new list)
		const toDelete = existingData.filter(
			(photo) => !newPhotoImageIds.includes(photo.imageID),
		);

		// Find photos to create (new photos)
		const toCreate = newPhotos.filter(
			(photo) =>
				!photo.id ||
				typeof photo.id !== "number" ||
				!existingImageIds.includes(photo.id),
		);

		// Delete removed ones (soft delete)
		for (const photo of toDelete) {
			await service.patch(photo.imageID, {
				isDeleted: true,
				deletedBy: userId,
				deletedDateTime: new Date().toISOString(),
			});
		}

		// Create new ones
		for (const photo of toCreate) {
			let imageData = photo.src;

			// Extract just the base64 string without the data URL prefix
			// The backend handles the conversion to binary
			if (
				typeof imageData === "string" &&
				imageData.startsWith("data:")
			) {
				imageData = imageData.split(",")[1];
			}

			await service.create({
				entityId: inspectionId,
				photoFor: PerPhotoType.Inspection,
				imageType: "Binary",
				imageData: imageData,
				fileName: photo.name || `photo-${Date.now()}.jpg`,
				notes: photo.notes || null,
				isThumbnail: false,
				isDeleted: false,
				createdBy: userId,
			});
		}

		return true;
	} catch (error) {
		console.error("Error saving inspection photos:", error);
		throw error;
	}
};

// Helper to fetch inspection photos
const fetchInspectionPhotos = async (inspectionId) => {
	try {
		const service = getService(serviceNames.personnelPhotos);
		const response = await service.find({
			query: {
				entityId: inspectionId,
				photoFor: PerPhotoType.Inspection,
				isDeleted: false,
			},
		});

		const photos = response || [];

		return photos.map((photo) => {
			let imageSrc = null;

			if (photo.imageData) {
				try {
					imageSrc = formatToBase64ImageUrl(photo.imageData);
				} catch (err) {
					console.error("Error converting image data:", err);
				}
			}

			return {
				id: photo.imageID,
				src: imageSrc,
				name: photo.fileName,
				size: 0,
				notes: photo.notes,
			};
		});
	} catch (error) {
		console.error("Error fetching inspection photos:", error);
		return [];
	}
};

// Helper to save inspection responses (same pattern as saveVehicleServiceRequirements)
const saveInspectionResponses = async (inspectionId, responses, userAgency) => {
	try {
		const service = getService(serviceNames.perInspectionResponses);

		// Get existing responses
		const existingResponses = await service.find({
			query: {
				inspectionId: inspectionId,
				isDeleted: false,
				$limit: -1,
			},
		});

		const existingData = existingResponses?.data || existingResponses || [];
		const existingItemIds = existingData.map((resp) => resp.checklistItemId);
		const newResponses = responses || [];
		const newItemIds = newResponses.map((r) => r.checklistItemId);

		// Find responses to delete
		const toDelete = existingData.filter(
			(resp) => !newItemIds.includes(resp.checklistItemId),
		);

		// Find responses to create
		const toCreate = newResponses.filter(
			(resp) => !existingItemIds.includes(resp.checklistItemId),
		);

		// Find responses to update
		const toUpdate = newResponses.filter((resp) =>
			existingItemIds.includes(resp.checklistItemId),
		);

		// Delete removed ones
		for (const resp of toDelete) {
			await service.patch(resp.id, { isDeleted: true });
		}

		// Create new ones
		for (const response of toCreate) {
			await service.create({
				inspectionId: inspectionId,
				checklistItemId: response.checklistItemId,
				status: response.status,
				comment: response.comment || null,
				agencyId: userAgency,
				isDeleted: false,
			});
		}

		// Update existing ones
		for (const response of toUpdate) {
			const existingResp = existingData.find(
				(r) => r.checklistItemId === response.checklistItemId,
			);
			if (existingResp) {
				await service.patch(existingResp.id, {
					status: response.status,
					comment: response.comment || null,
				});
			}
		}

		return true;
	} catch (error) {
		console.error("Error saving inspection responses:", error);
		throw error;
	}
};

export const getInspectionsList =
	(
		page = 0,
		pageSize = 20,
		sortField = "id",
		sortDirection = "desc",
		search = "",
		filters = {},
		userAgency,
	) =>
	async (dispatch) => {
		const service = getService(serviceNames.perInspections);
		const query = {
			$limit: pageSize,
			$skip: page * pageSize,
			$sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
			isDeleted: false,
			agencyId: userAgency,
		};

		// Text search
		if (search) {
			query.$or = [
				{ id: { $like: `%${search}%` } },
				{ type: { $like: `%${search}%` } },
			];
		}

		// Helper function
		const getQueryValue = (input) => {
			if (Array.isArray(input)) {
				const values = input.map((i) => i.value).filter(Boolean);
				return values.length > 0 ? { $in: values } : undefined;
			} else if (input?.value) {
				return input.value;
			}
			return undefined;
		};

		// Multi-select filter fields
		const multiSelectFields = [
			"vehicleId",
			"type",
			"perPersonnelId",
			"overallStatus",
		];

		multiSelectFields.forEach((field) => {
			const value = getQueryValue(filters[field]);
			if (value !== undefined) {
				query[field] = value;
			}
		});

		// Status filter
		if (filters.status?.value && filters.status.value !== "all") {
			query.overallStatus = filters.status.value;
		}

		// Date range filters
		if (filters.from) {
			query.date = { ...(query.date || {}), $gte: filters.from };
		}
		if (filters.to) {
			query.date = { ...(query.date || {}), $lte: filters.to };
		}

		// Period filter
		if (filters.period?.value) {
			const now = new Date();
			let startDate;

			switch (filters.period.value) {
				case "today":
					startDate = new Date(now.setHours(0, 0, 0, 0));
					break;
				case "week":
					startDate = new Date(now.setDate(now.getDate() - 7));
					break;
				case "month":
					startDate = new Date(now.setMonth(now.getMonth() - 1));
					break;
				case "quarter":
					startDate = new Date(now.setMonth(now.getMonth() - 3));
					break;
				case "year":
					startDate = new Date(
						now.setFullYear(now.getFullYear() - 1),
					);
					break;
				default:
					break;
			}

			if (startDate) {
				query.date = {
					...(query.date || {}),
					$gte: startDate.toISOString(),
				};
			}
		}

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: FETCH_INSPECTIONS_REQUEST,
				success: FETCH_INSPECTIONS_SUCCESS,
				failure: FETCH_INSPECTIONS_FAILURE,
			},
			apiCall: async () => {
				const response = await service.find({ query });
				const inspectionsData = response?.data || response || [];

				// Fetch responses for each inspection
				const responsesService = getService(
					serviceNames.perInspectionResponses,
				);

				const inspectionsWithDetails = await Promise.all(
					inspectionsData.map(async (inspection) => {
						try {
							// Get responses
							const responsesResponse =
								await responsesService.find({
									query: {
										inspectionId: inspection.id,
										isDeleted: false,
										$limit: -1,
									},
								});

							const responses =
								responsesResponse?.data ||
								responsesResponse ||
								[];

							// Get photos using personnel-photos service
							const photos = await fetchInspectionPhotos(
								inspection.id,
							);

							return {
								...inspection,
								responses: responses.map((r) => ({
									checklistItemId: r.checklistItemId,
									status: r.status,
									comment: r.comment,
								})),
								photos: photos,
							};
						} catch (error) {
							console.error(
								`Error fetching inspection details for ${inspection.id}:`,
								error,
							);
							return {
								...inspection,
								responses: [],
								photos: [],
							};
						}
					}),
				);

				return {
					inspections: inspectionsWithDetails,
					total: response.total || inspectionsWithDetails.length,
				};
			},
			transformResult: (res) => ({
				inspections: res?.inspections,
				total: res?.total,
			}),
			errorMessagePrefix: "Failed to fetch inspections",
		});
	};

export const createInspection =
	(inspectionData, responses = [], photos = [], userAgency, userId) =>
	async (dispatch) => {
		const service = getService(serviceNames.perInspections);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CREATE_INSPECTION_REQUEST,
				success: CREATE_INSPECTION_SUCCESS,
				failure: CREATE_INSPECTION_FAILURE,
			},
			apiCall: async () => {
				const inspection = await service.create({
					...inspectionData,
					agencyId: userAgency,
					createdBy: userId,
					isDeleted: false,
				});

				// Save responses
				if (responses.length > 0 && inspection.id) {
					await saveInspectionResponses(
						inspection.id,
						responses,
						userAgency,
					);
				}

				// Save photos using personnel-photos service
				if (photos.length > 0 && inspection.id) {
					await saveInspectionPhotos(
						inspection.id,
						photos,
						userAgency,
						userId,
					);
				}

				// Update vehicle odometer
				if (inspectionData.odometer && inspectionData.vehicleId) {
					const vehicleService = getService(serviceNames.perVehicles);
					await vehicleService.patch(inspectionData.vehicleId, {
						odometer: inspectionData.odometer,
						lastMileage: inspectionData.odometer,
					});
				}

				return {
					...inspection,
					responses,
					photos,
				};
			},
			successMessage: "Inspection created successfully",
			errorMessagePrefix: "Failed to create inspection",
		});
	};

export const updateInspection =
	(id, inspectionData, responses = [], photos = [], userAgency, userId) =>
	async (dispatch) => {
		const service = getService(serviceNames.perInspections);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_INSPECTION_REQUEST,
				success: UPDATE_INSPECTION_SUCCESS,
				failure: UPDATE_INSPECTION_FAILURE,
			},
			apiCall: async () => {
				const inspection = await service.patch(id, {
					...inspectionData,
					updatedBy: userId,
				});

				// Update responses
				await saveInspectionResponses(id, responses, userAgency);

				// Update photos using personnel-photos service
				await saveInspectionPhotos(id, photos, userAgency, userId);

				// Update vehicle odometer
				if (inspectionData.odometer && inspectionData.vehicleId) {
					const vehicleService = getService(serviceNames.perVehicles);
					await vehicleService.patch(inspectionData.vehicleId, {
						odometer: inspectionData.odometer,
						lastMileage: inspectionData.odometer,
					});
				}

				// Fetch updated photos
				const updatedPhotos = await fetchInspectionPhotos(id);

				return {
					...inspection,
					responses,
					photos: updatedPhotos,
				};
			},
			successMessage: "Inspection updated successfully",
			errorMessagePrefix: "Failed to update inspection",
		});
	};

export const resolveInspection =
	(id, resolveData, userId) => async (dispatch) => {
		const service = getService(serviceNames.perInspections);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_INSPECTION_REQUEST,
				success: UPDATE_INSPECTION_SUCCESS,
				failure: UPDATE_INSPECTION_FAILURE,
			},
			apiCall: async () => {
				const inspection = await service.patch(id, {
					resolvedAt: resolveData.resolvedAt,
					resolvedBy: userId,
					resolvedComments: resolveData.resolvedComments,
					updatedBy: userId,
				});
				return inspection;
			},
			successMessage: "Inspection resolved successfully",
			errorMessagePrefix: "Failed to resolve inspection",
		});
	};

export const deleteInspection = (id, userId) => async (dispatch) => {
	const service = getService(serviceNames.perInspections);
	const patchData = {
		isDeleted: true,
		deletedBy: userId,
		deletedDateTime: new Date().toISOString(),
	};

	return await asyncThunkWrapper({
		dispatch,
		types: {
			request: DELETE_INSPECTION_REQUEST,
			success: DELETE_INSPECTION_SUCCESS,
			failure: DELETE_INSPECTION_FAILURE,
		},
		apiCall: async () => {
			await service.patch(id, patchData);

			// Soft delete responses
			const responsesService = getService(
				serviceNames.perInspectionResponses,
			);
			const responses = await responsesService.find({
				query: { inspectionId: id, isDeleted: false, $limit: -1 },
			});

			const existingResponses = responses?.data || responses || [];
			for (const resp of existingResponses) {
				await responsesService.patch(resp.id, { isDeleted: true });
			}

			// Soft delete photos using personnel-photos service
			const photosService = getService(serviceNames.personnelPhotos);
			const photos = await photosService.find({
				query: {
					entityId: id,
					photoFor: PerPhotoType.Inspection,
					isDeleted: false,
				},
			});

			const existingPhotos = photos || [];
			for (const photo of existingPhotos) {
				await photosService.patch(photo.imageID, {
					isDeleted: true,
					deletedBy: userId,
					deletedDateTime: new Date().toISOString(),
				});
			}

			return id;
		},
		transformResult: () => id,
		successMessage: "Inspection deleted successfully",
		errorMessagePrefix: "Failed to delete inspection",
	});
};

export default function inspectionsReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_INSPECTIONS_REQUEST:
		case CREATE_INSPECTION_REQUEST:
		case UPDATE_INSPECTION_REQUEST:
		case DELETE_INSPECTION_REQUEST:
			return { ...state, loading: true, error: null };
		case FETCH_INSPECTIONS_SUCCESS:
			return {
				...state,
				loading: false,
				inspections: action.payload.inspections,
				total: action.payload.total,
			};
		case CREATE_INSPECTION_SUCCESS:
			return {
				...state,
				loading: false,
				inspections: [action.payload, ...state.inspections],
				total: state.total + 1,
			};
		case UPDATE_INSPECTION_SUCCESS:
			return {
				...state,
				loading: false,
				inspections: state.inspections.map((i) =>
					i.id === action.payload.id ? action.payload : i,
				),
			};
		case DELETE_INSPECTION_SUCCESS:
			return {
				...state,
				loading: false,
				inspections: state.inspections.filter(
					(i) => i.id !== action.payload,
				),
				total: state.total - 1,
			};
		case FETCH_INSPECTIONS_FAILURE:
		case CREATE_INSPECTION_FAILURE:
		case UPDATE_INSPECTION_FAILURE:
		case DELETE_INSPECTION_FAILURE:
			return { ...state, loading: false, error: action.payload };
		default:
			return state;
	}
}
